import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["select", "container", "template", "placeholder"]
  static values = {
    existingWeights: String, // JSON string
    modelKey: String, // e.g. "matching_assignment"
    weightsKey: String, // e.g. "job_profile_weights"
  }

  connect() {
    this.sync()
  }

  updateWeights(weights) {
    this.existingWeightsValue = JSON.stringify(weights)
    this.sync()
  }

  sync() {
    const selectedValues = new Set(
      Array.from(this.selectTarget.selectedOptions).map((o) => o.value),
    )

    // Add missing weight inputs
    selectedValues.forEach((value) => {
      if (!this._rowFor(value)) this._addRow(value)
    })

    // Remove rows for values no longer selected
    Array.from(
      this.containerTarget.querySelectorAll("[data-weight-for]"),
    ).forEach((row) => {
      const value = row.dataset.weightFor
      if (!selectedValues.has(value)) row.remove()
    })

    this._renumberRows()
    this._togglePlaceholder()
  }

  _togglePlaceholder() {
    if (!this.hasPlaceholderTarget) return
    const hasRows =
      this.containerTarget.querySelectorAll("[data-weight-for]").length > 0
    this.placeholderTarget.classList.toggle("hidden", hasRows)
  }

  _addRow(value) {
    const labelText = this._optionLabel(value)
    const existing = this._existingWeights()[value]
    const weightValue = this._sanitizeWeight(existing ?? 1)
    const radioName = `${this.modelKeyValue}[${this.weightsKeyValue}][${value}]`

    const clone = this.templateTarget.content.cloneNode(true)
    const row = clone.firstElementChild
    row.dataset.weightFor = value

    row.querySelector('[data-role="label"]').textContent = `${labelText}:`
    row.dataset.labelText = labelText

    row.querySelectorAll('[data-role="radio"]').forEach((radio) => {
      radio.name = radioName
      if (parseInt(radio.value, 10) === weightValue) radio.checked = true
    })

    if (this.hasPlaceholderTarget) {
      this.containerTarget.insertBefore(clone, this.placeholderTarget)
    } else {
      this.containerTarget.appendChild(clone)
    }
  }

  _existingWeights() {
    try {
      if (!this.hasExistingWeightsValue || !this.existingWeightsValue) return {}
      const parsed = JSON.parse(this.existingWeightsValue)
      return parsed && typeof parsed === "object" ? parsed : {}
    } catch {
      return {}
    }
  }

  _sanitizeWeight(v) {
    let n = parseInt(v, 10)
    if (Number.isNaN(n)) n = 1
    if (n < 1) n = 1
    if (n > 3) n = 3
    return n
  }

  _renumberRows() {
    this.containerTarget
      .querySelectorAll("[data-weight-for]")
      .forEach((row, index) => {
        const label = row.querySelector('[data-role="label"]')
        label.textContent = `${index + 1}. ${row.dataset.labelText}:`
      })
  }

  _rowFor(value) {
    return this.containerTarget.querySelector(
      `[data-weight-for="${CSS.escape(value)}"]`,
    )
  }

  _optionLabel(value) {
    const opt = Array.from(this.selectTarget.options).find(
      (o) => o.value === value,
    )
    return opt ? opt.text : value
  }
}
